import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Clock, Package, RefreshCw, Truck, Search, Layers, CheckCircle2 } from 'lucide-react';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';
import PhonePlates from './Plates/PhonePlates';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Plates, longest wait first.
 *
 * A plate is made outside, so this screen records the two moments nobody on the
 * floor can scan: it went out, and it came back. The tabs are the states it
 * passes through — Pending, Ordered, Received — with All last, the way the job
 * cards and orders screens are laid out.
 *
 * Plates are ordered from one vendor in batches, so the action this exists for
 * is "these six went out today". Rows can be ticked and marked together rather
 * than clicked one at a time down the Action column.
 */

const STATUS = {
  pending: { label: 'Pending', className: 'text-amber-700' },
  ordered: { label: 'Ordered', className: 'text-blue-700' },
  received: { label: 'Received', className: 'text-emerald-700' },
};

// All sits last: the working tabs come first, the archive behind them
const TABS = [
  { key: 'pending', label: 'Pending', icon: Clock, tone: 'warn' },
  { key: 'ordered', label: 'Ordered', icon: Package },
  { key: 'received', label: 'Received', icon: CheckCircle2, tone: 'good' },
  { key: 'all', label: 'All plates', icon: Layers },
];

const SORTS = [
  ['days_waiting', 'Longest waiting'],
  ['order_date', 'Order date'],
  ['delivery_date', 'Delivery date'],
];

export default function PlateTrackingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  const [tab, setTab] = useState(searchParams.get('tab') || 'pending');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [searchDebounce, setSearchDebounce] = useState(search);
  const [plates, setPlates] = useState([]);
  const [counts, setCounts] = useState({});
  const [canMark, setCanMark] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('days_waiting');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [tab, searchDebounce]);

  const fetchPlates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50, sort_by: sortBy, status: tab };
      if (searchDebounce) params.search = searchDebounce;
      const res = await api.get('/plates/tracking', { params });
      setPlates(res.data.plates || []);
      setCounts(res.data.counts || {});
      setCanMark(res.data.can_mark);
      setTotal(res.data.total || 0);
      setPicked(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load plate tracking');
    }
    setLoading(false);
  }, [page, sortBy, tab, searchDebounce]);

  useEffect(() => { fetchPlates(); }, [fetchPlates]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (tab !== 'pending') params.set('tab', tab);
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [tab, search, setSearchParams]);

  const mark = useCallback(async (rows, action) => {
    if (rows.length === 0) return;
    setBusy(true);
    const results = await Promise.allSettled(rows.map(
      r => api.post(`/plates/orders/${r.order_uuid}/items/${r.line_item_id}/mark-${action}`, {})
    ));
    const failed = results.filter(r => r.status === 'rejected').length;
    if (failed) {
      toast.error(`${failed} of ${rows.length} could not be marked ${action}`);
    } else {
      toast.success(`${rows.length} plate${rows.length === 1 ? '' : 's'} marked ${action}`);
    }
    setBusy(false);
    fetchPlates();
  }, [fetchPlates]);

  const togglePick = (id) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const pickedRows = useMemo(
    () => plates.filter(p => picked.has(p.line_item_id)),
    [plates, picked]
  );
  const canOrder = pickedRows.filter(p => p.plate_status === 'pending');
  const canReceive = pickedRows.filter(p => p.plate_status === 'ordered');
  const pickable = plates.filter(p => p.plate_status !== 'received');

  const columns = useMemo(() => ([
    ...(canMark ? [{
      key: 'pick',
      header: '',
      width: '2.25rem',
      render: (plate) => plate.plate_status === 'received' ? null : (
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={picked.has(plate.line_item_id)}
            onCheckedChange={() => togglePick(plate.line_item_id)}
            aria-label={`Select ${plate.item_name || plate.order_id}`}
            className="h-3.5 w-3.5"
          />
        </span>
      ),
    }] : []),
    {
      key: 'item',
      header: 'Item / Order',
      width: 'minmax(12rem,1.4fr)',
      render: (plate) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{plate.item_name || '—'}</span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">{plate.order_id}</span>
        </>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(10rem,1fr)',
      render: (plate) => (
        <>
          <span className="block truncate text-slate-700">{plate.customer_name}</span>
          {plate.firm_name && <span className="block truncate text-[11px] leading-tight text-slate-400">{plate.firm_name}</span>}
        </>
      ),
    },
    {
      key: 'product_type',
      header: 'Type',
      width: '7rem',
      render: (plate) => <span className="block truncate text-[11px] text-slate-600">{plate.product_type}</span>,
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (plate) => <span className="font-medium">{plate.quantity?.toLocaleString() || '—'}</span>,
      total: (rows) => rows.reduce((s, p) => s + (p.quantity || 0), 0).toLocaleString(),
    },
    {
      key: 'plate_status',
      header: 'Status',
      width: '7.5rem',
      render: (plate) => {
        const s = STATUS[plate.plate_status] || STATUS.pending;
        return (
          <span className="flex min-w-0 items-center gap-1.5">
            <span className={`text-[12px] font-medium ${s.className}`}>{s.label}</span>
            {/* the job is standing at Plate Making, not merely waiting on one */}
            {plate.holding_production && plate.plate_status !== 'received' && (
              <span className="shrink-0 rounded bg-red-50 px-1 text-[9px] font-semibold text-red-600">
                holding
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: 'plate_ordered_at',
      header: 'Ordered',
      width: '9.5rem',
      render: (plate) => plate.plate_ordered_at ? (
        <span className="block truncate text-[11px] text-slate-600"
          title={plate.plate_ordered_by_name ? `by ${plate.plate_ordered_by_name}` : undefined}>
          {formatIST(plate.plate_ordered_at, { compact: true })}
        </span>
      ) : <span className="text-[11px] text-slate-400">—</span>,
    },
    {
      key: 'plate_received_at',
      header: 'Received',
      width: '9.5rem',
      render: (plate) => plate.plate_received_at ? (
        <span className="block truncate text-[11px] text-slate-600"
          title={plate.plate_received_by_name ? `by ${plate.plate_received_by_name}` : undefined}>
          {formatIST(plate.plate_received_at, { compact: true })}
        </span>
      ) : <span className="text-[11px] text-slate-400">—</span>,
    },
    {
      key: 'days_waiting',
      header: 'Waiting',
      align: 'right',
      width: '5rem',
      render: (plate) => (plate.plate_status !== 'received' && plate.days_waiting > 0) ? (
        <span className={`font-semibold ${
          plate.days_waiting > 3 ? 'text-red-600' : plate.days_waiting > 1 ? 'text-amber-600' : 'text-slate-600'
        }`}>
          {plate.days_waiting}d
        </span>
      ) : <span className="text-slate-400">—</span>,
    },
    {
      key: 'delivery_date',
      header: 'Due',
      width: '6.5rem',
      render: (plate) => (
        <span className="whitespace-nowrap text-[11px] text-slate-600">
          {plate.delivery_date ? formatIST(plate.delivery_date, { dateOnly: true }) : '—'}
        </span>
      ),
    },
    ...(canMark ? [{
      key: 'actions',
      header: '',
      width: '9rem',
      align: 'right',
      render: (plate) => (
        <span className="flex items-center justify-end gap-0.5">
          {plate.plate_status === 'pending' && (
            <CellButton
              icon={Package}
              label="Mark ordered"
              disabled={busy}
              onClick={() => mark([plate], 'ordered')}
            />
          )}
          {plate.plate_status === 'ordered' && (
            <CellButton
              icon={Truck}
              label="Mark received"
              tone="good"
              disabled={busy}
              onClick={() => mark([plate], 'received')}
            />
          )}
          {plate.plate_status === 'received' && (
            <span className="px-1 text-[11px] font-medium text-emerald-600">Done</span>
          )}
        </span>
      ),
    }] : []),
  ]), [picked, busy, mark, canMark]);

  const totalPages = Math.ceil(total / 50);

  // one card per plate below lg; the same tabs, the same marking calls
  if (isMobile) {
    return (
      <PhonePlates
        plates={plates} loading={loading} total={total} counts={counts}
        tabs={TABS} tab={tab} onTab={setTab}
        search={search} onSearch={setSearch} sorts={SORTS} sortBy={sortBy} onSort={setSortBy} onRefresh={fetchPlates}
        page={page} totalPages={totalPages} onPage={setPage}
        canMark={canMark} picked={picked} onPick={togglePick}
        onPickAll={() => setPicked(picked.size ? new Set() : new Set(pickable.map(p => p.line_item_id)))}
        pickable={pickable} canOrder={canOrder} canReceive={canReceive} busy={busy} onMark={mark}
        onOpen={(plate) => navigate(`/orders/${plate.order_uuid}`)}
      />
    );
  }

  return (
    <Screen data-testid="plate-tracking-page">
      <CommandBar
        title="Plate Tracking"
        subtitle={picked.size ? `${picked.size} selected` : `${total} plate${total === 1 ? '' : 's'}`}
        actions={[
          canOrder.length > 0 && {
            key: 'bulk-order',
            label: `Mark ${canOrder.length} ordered`,
            icon: Package,
            tone: 'primary',
            disabled: busy,
            onClick: () => mark(canOrder, 'ordered'),
          },
          canReceive.length > 0 && {
            key: 'bulk-receive',
            label: `Mark ${canReceive.length} received`,
            icon: Truck,
            tone: 'good',
            disabled: busy,
            onClick: () => mark(canReceive, 'received'),
          },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchPlates, disabled: loading },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Order, customer, firm…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label="Sort plates by"
          className="h-7 rounded border border-slate-200 bg-white px-2 text-xs text-slate-700"
        >
          {SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </CommandBar>

      <StatStrip
        stats={TABS.map(t => ({
          key: t.key,
          label: t.label,
          value: counts[t.key] ?? 0,
          tone: t.key === 'pending' && counts.pending ? 'warn'
            : t.key === 'received' ? 'good' : 'default',
        }))}
        activeKey={tab}
        onSelect={(key) => setTab(key || 'pending')}
      >
        {counts.overdue > 0 && (
          <span className="flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-[11px] font-medium text-red-600">
            {counts.overdue} waiting over 3d
          </span>
        )}
      </StatStrip>

      <Pane
        title="Plates"
        aside={TABS.find(t => t.key === tab)?.label.toLowerCase() || 'all'}
        scroll={false}
        bodyClassName="flex flex-col"
        actions={
          <span className="flex items-center gap-2">
            {canMark && pickable.length > 0 && (
              <button
                type="button"
                onClick={() => setPicked(picked.size ? new Set() : new Set(pickable.map(p => p.line_item_id)))}
                className="rounded px-1.5 py-0.5 text-[10px] font-normal normal-case tracking-normal text-blue-100 hover:bg-white/20"
              >
                {picked.size ? 'Clear' : 'Select all'}
              </button>
            )}
            {totalPages > 1 && (
              <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
                {page} / {totalPages}
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
              </span>
            )}
          </span>
        }
      >
        <DataGrid
          data-testid="plates-table"
          columns={columns}
          rows={plates}
          loading={loading}
          getRowId={(plate) => plate.line_item_id}
          onRowClick={(plate) => navigate(`/orders/${plate.order_uuid}`)}
          showTotals={plates.length > 0}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <Clock className="h-8 w-8 text-slate-300" />
              {tab === 'pending' ? 'No plates waiting to be ordered' : 'No plates here'}
            </span>
          }
        />
      </Pane>
    </Screen>
  );
}
